import { useEffect, useRef, useCallback, RefObject } from 'react';

type UseInfiniteScrollOptions = {
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
  root?: RefObject<Element>;
};

export const useInfiniteScroll = ({
  loading,
  hasMore,
  onLoadMore,
  rootMargin = '200px',
  threshold = 0,
  root,
}: UseInfiniteScrollOptions) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const setSentinelRef = useCallback((node: HTMLDivElement | null) => {
    sentinelRef.current = node;
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          onLoadMore();
        }
      },
      { rootMargin, threshold, root: root?.current ?? null }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [loading, hasMore, onLoadMore, rootMargin, threshold, root]);

  return { sentinelRef: setSentinelRef };
};
